import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, RefreshCcw, Share2, X } from 'lucide-react';
import ShareStatsDialog from '../components/ShareStatsDialog';
import { Button } from '../components/ui/button';
import DonutChart from '../components/DonutChart';
import { ApiService } from '../utils/api';
import { formatTime } from '../utils/datetime';
import { conditionKey, conditionLabel, conditionsSearch, filtersFromSearch, STATUS_LABELS } from '../utils/registrationFilterParams';
import { useEventRoute } from '../utils/eventRef';
import { useAuth } from '../contexts/AuthContext';
import { resolveRoleUi } from '../utils/roleUi';

const POLL_MS = 10000;
const NOT_ANSWERED = 'Not answered';

const StatTile = ({ label, value }) => (
  <div className="rounded-lg border bg-white p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-2xl font-semibold text-gray-900 tabular-nums">{(value ?? 0).toLocaleString()}</p>
  </div>
);

const formatInr = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const METHOD_LABELS = { upi: 'UPI', bank: 'Bank transfer', 'not specified': 'Not specified' };
const SOURCE_LABELS = { ONLINE: 'Online', 'ON-SPOT': 'On-spot', ON_SPOT: 'On-spot' };

const MoneyTile = ({ label, amount, note, tone = 'text-gray-900' }) => (
  <div className="rounded-lg border bg-white p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-2xl font-semibold tabular-nums ${tone}`}>{amount}</p>
    {note && <p className="mt-0.5 text-xs text-gray-500">{note}</p>}
  </div>
);

const AmountTable = ({ title, rows, labels }) => (
  <div className="rounded-lg border bg-white p-4">
    <h3 className="mb-2 text-sm font-semibold text-gray-900">{title}</h3>
    {rows.length === 0 ? (
      <p className="text-sm text-gray-500">No payments received yet</p>
    ) : (
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500">
            <th className="py-1 font-medium">&nbsp;</th>
            <th className="py-1 text-right font-medium">Registrations</th>
            <th className="py-1 text-right font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.value} className="border-t">
              <td className="py-1.5 text-gray-700">{labels[row.value] || row.value}</td>
              <td className="py-1.5 text-right tabular-nums">{row.count.toLocaleString('en-IN')}</td>
              <td className="py-1.5 text-right font-medium tabular-nums">{formatInr(row.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const EventAnalytics = () => {
  const { eventId, eventRef } = useEventRoute();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: event } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => ApiService.getEvent(eventId),
    enabled: !!eventId,
  });

  const { data: analytics, isLoading, isFetching, error } = useQuery({
    queryKey: ['eventAnalytics', eventId],
    queryFn: () => ApiService.getEventAnalytics(eventId),
    enabled: !!eventId,
    refetchInterval: POLL_MS,
  });

  // Clicking slices builds an AND-ed condition set; one condition per filter key (a new value replaces it).
  const [selected, setSelected] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const isSelected = (target) => selected.some((s) => conditionKey(s) === conditionKey(target) && s.value === target.value);
  const toggle = (target) => setSelected((prev) => (isSelected(target)
    ? prev.filter((s) => conditionKey(s) !== conditionKey(target))
    : [...prev.filter((s) => conditionKey(s) !== conditionKey(target)), target]));
  const drillDown = (target) => () => toggle(target);

  const { data: inviteStats = [] } = useQuery({
    queryKey: ['inviteLinks', eventId],
    queryFn: () => ApiService.getInviteLinks(eventId),
    enabled: !!eventId,
  });

  const { data: scannerCounts = [] } = useQuery({
    queryKey: ['scansByScanner', eventId],
    queryFn: () => ApiService.getScansByScanner(eventId),
    enabled: !!eventId,
    refetchInterval: 30000,
  });
  const selectionSearch = conditionsSearch(selected);
  const selectionFilters = filtersFromSearch(selectionSearch).filters;

  const { data: selectionPage, isFetching: selectionCounting } = useQuery({
    queryKey: ['analyticsSelectionCount', eventId, selectionSearch],
    queryFn: () => ApiService.getFilteredRegistrations(eventId, { filters: selectionFilters, page: 0, size: 1 }),
    enabled: !!eventId && selected.length > 0,
  });

  const refresh = async () => {
    const fresh = await ApiService.getEventAnalytics(eventId, true);
    queryClient.setQueryData(['eventAnalytics', eventId], fresh);
  };

  const count = (slices, value) => (slices || []).find((s) => s.value === value)?.count || 0;
  const statusSlices = (analytics?.statusCounts || []).map((s) => ({
    key: s.value ?? '__none',
    label: STATUS_LABELS[s.value] || s.value || 'Unknown',
    count: s.count,
    onSelect: s.value ? drillDown({ type: 'status', value: s.value }) : undefined,
    selected: !!s.value && isSelected({ type: 'status', value: s.value }),
  }));
  const sourceSlices = (analytics?.sourceCounts || []).map((s) => ({
    key: s.value ?? '__none',
    label: s.value || 'Unknown',
    count: s.count,
    onSelect: s.value ? drillDown({ type: 'source', value: s.value }) : undefined,
    selected: !!s.value && isSelected({ type: 'source', value: s.value }),
  }));
  const scanSlices = (analytics?.scanCounts || []).map((s) => ({
    key: s.purpose,
    label: s.deleted ? `${s.name} (deleted)` : s.name,
    count: s.totalScans,
    onSelect: drillDown({ type: 'checkin', value: s.purpose }),
    selected: isSelected({ type: 'checkin', value: s.purpose }),
  }));
  const inviteSlices = inviteStats.map(({ link, registrations }) => ({
    key: link.id,
    label: link.name,
    count: registrations,
    onSelect: drillDown({ type: 'invite', value: link.id, name: link.name }),
    selected: isSelected({ type: 'invite', value: link.id }),
  }));
  const purposeNames = Object.fromEntries((analytics?.scanCounts || []).map((s) => [s.purpose, s.name]));
  const totalScans = (analytics?.scanCounts || []).reduce((sum, s) => sum + (s.totalScans || 0), 0);

  const roleUi = resolveRoleUi(event?.roleUi, user?.roles || []);
  const canOpenRegistrations = roleUi.can('registrationsPage');
  if (event && !roleUi.can('analytics')) {
    return <p className="p-8 text-center text-gray-600">Analytics isn't available for your role.</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {canOpenRegistrations && (
            <Link to={`/admin/events/${eventRef}/registrations`}>
              <Button variant="outline" size="sm"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
            </Link>
          )}
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{event?.name || 'Event'} analytics</h1>
            {analytics?.computedAt && (
              <p className="text-xs text-gray-500">Updated {formatTime(analytics.computedAt)} · refreshes every 10s</p>
            )}
            <p className="text-xs text-gray-500">Select slices across charts to combine them as filters.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowShare(true)}>
            <Share2 className="w-4 h-4 mr-1" />
            Share live stats
          </Button>
          <Button variant="outline" size="sm" onClick={refresh} disabled={isFetching}>
            <RefreshCcw className={`w-4 h-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh now
          </Button>
        </div>
        <ShareStatsDialog open={showShare} onClose={() => setShowShare(false)} eventId={eventId} />
      </div>

      {selected.length > 0 && (
        <div className="sticky top-0 z-10 rounded-lg border border-blue-200 bg-blue-50 p-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex flex-wrap items-center gap-2 text-sm flex-1">
            <span className="text-gray-700">Registrations matching all of</span>
            {selected.map((target) => (
              <span key={conditionKey(target)} className="inline-flex items-center gap-1 rounded-full bg-white border border-blue-200 pl-3 pr-1 py-0.5 text-blue-800">
                {conditionLabel(target, purposeNames)}
                <button type="button" onClick={() => toggle(target)} className="rounded-full p-0.5 hover:bg-blue-100" aria-label="Remove condition">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected([])}>Clear</Button>
            {canOpenRegistrations && (
              <Button size="sm" onClick={() => navigate(`/admin/events/${eventRef}/registrations?${selectionSearch}`)}>
                View {selectionCounting || selectionPage?.totalElements == null ? '' : `${selectionPage.totalElements.toLocaleString()} `}registrations
              </Button>
            )}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <p className="text-red-600">Failed to load analytics: {error.message}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile label="Registrations" value={analytics?.totalRegistrations} />
            <StatTile label="Pending approval" value={count(analytics?.statusCounts, 'PENDING_FOR_APPROVAL')} />
            <StatTile label="Pending payment" value={count(analytics?.statusCounts, 'PENDING_PAYMENT')} />
            <StatTile label="Total QR scans" value={totalScans} />
          </div>

          {analytics?.revenue && (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Payments</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MoneyTile
                  label="Amount received"
                  amount={formatInr(analytics.revenue.receivedAmount)}
                  note={`${analytics.revenue.paidCount.toLocaleString('en-IN')} paid registrations`}
                  tone="text-green-700"
                />
                <MoneyTile
                  label="Pending payment / approval"
                  amount={formatInr(analytics.revenue.pendingAmount)}
                  note={`${analytics.revenue.pendingCount.toLocaleString('en-IN')} registrations`}
                  tone="text-amber-700"
                />
                <MoneyTile
                  label="Free registrations"
                  amount={analytics.revenue.freeCount.toLocaleString('en-IN')}
                  note="Nothing to pay (free category or coupon)"
                />
                <MoneyTile
                  label="Coupon discounts"
                  amount={formatInr(analytics.revenue.discountAmount)}
                  note="On paid and pending registrations"
                />
              </div>
              {analytics.revenue.paidCount > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <AmountTable title="Received by source" rows={analytics.revenue.receivedBySource || []} labels={SOURCE_LABELS} />
                  <AmountTable title="Received by payment method" rows={analytics.revenue.receivedByMethod || []} labels={METHOD_LABELS} />
                </div>
              )}
            </section>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DonutChart title="Registrations by status" slices={statusSlices} />
            <DonutChart
              title="QR scans by purpose"
              slices={scanSlices}
              emptyText="No scans yet"
              footnote="Counts include manual adjustments; selecting a slice filters registrations checked in for that purpose."
            />
            <DonutChart title="Registrations by source" slices={sourceSlices} />
            {inviteSlices.length > 0 && (
              <DonutChart
                title="Registrations by invite link"
                slices={inviteSlices}
                footnote="Selecting a slice filters registrations made through that invite link."
              />
            )}
            {(analytics?.fields || []).map((field) => (
              <DonutChart
                key={`${field.parentLabel || ''}/${field.label}`}
                title={field.parentLabel ? `${field.label} (${field.parentLabel})` : field.label}
                slices={(field.counts || []).map((s) => ({
                  key: s.value ?? '__none',
                  label: s.value ?? NOT_ANSWERED,
                  count: s.count,
                  onSelect: s.value != null ? drillDown({ type: 'field', field: field.label, value: s.value }) : undefined,
                  selected: s.value != null && isSelected({ type: 'field', field: field.label, value: s.value }),
                }))}
              />
            ))}
          </div>

          {scannerCounts.length > 0 && (
            <section className="rounded-lg border bg-white p-4">
              <h3 className="mb-1 text-sm font-semibold text-gray-900">Check-ins by scanner</h3>
              <p className="mb-3 text-xs text-gray-500">
                Who recorded each check-in: the scanner name set on the device, otherwise the signed-in user.
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {Object.entries(scannerCounts.reduce((groups, row) => {
                  (groups[row.purpose] = groups[row.purpose] || []).push(row);
                  return groups;
                }, {})).map(([purpose, rows]) => (
                  <table key={purpose} className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500">
                        <th className="py-1 font-medium">{purposeNames[purpose] || purpose}</th>
                        <th className="py-1 text-right font-medium">Check-ins</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.scanner} className="border-t">
                          <td className={`py-1.5 ${row.scanner === 'Not set' ? 'text-gray-400' : 'text-gray-700'}`}>{row.scanner}</td>
                          <td className="py-1.5 text-right tabular-nums">{row.count.toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))}
              </div>
            </section>
          )}

          {inviteStats.length > 0 && (
            <section className="rounded-lg border bg-white p-4">
              <h3 className="mb-1 text-sm font-semibold text-gray-900">Registrations by invite link</h3>
              <p className="mb-3 text-xs text-gray-500">Click a row to open those registrations. Registered excludes cancelled, rejected and unpaid; Used counts every place taken.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500">
                      <th className="py-1 font-medium">Invited</th>
                      <th className="py-1 text-right font-medium">Registered</th>
                      <th className="py-1 text-right font-medium">Used</th>
                      <th className="py-1 text-right font-medium">Limit</th>
                      <th className="py-1 text-right font-medium">Cancelled</th>
                      <th className="py-1 pl-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inviteStats.map(({ link, registrations, byStatus }) => (
                      <tr
                        key={link.id}
                        className={`border-t ${canOpenRegistrations ? 'cursor-pointer hover:bg-blue-50' : ''}`}
                        onClick={canOpenRegistrations ? () => navigate(`/admin/events/${eventRef}/registrations?${conditionsSearch([{ type: 'invite', value: link.id, name: link.name }])}`) : undefined}
                        title={canOpenRegistrations ? 'Show these registrations' : undefined}
                      >
                        <td className="py-1.5 font-medium text-blue-700">{link.name}</td>
                        <td className="py-1.5 text-right tabular-nums">{registrations.toLocaleString('en-IN')}</td>
                        <td className="py-1.5 text-right tabular-nums">{(link.usedCount || 0).toLocaleString('en-IN')}</td>
                        <td className="py-1.5 text-right tabular-nums text-gray-500">{link.maxUses ?? '—'}</td>
                        <td className="py-1.5 text-right tabular-nums text-gray-500">{((byStatus?.CANCELLED || 0) + (byStatus?.REJECTED || 0)).toLocaleString('en-IN')}</td>
                        <td className="py-1.5 pl-3 text-xs text-gray-500">{link.active ? 'Active' : 'Off'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t font-medium">
                      <td className="py-1.5">All invite links</td>
                      <td className="py-1.5 text-right tabular-nums">{inviteStats.reduce((sum, row) => sum + row.registrations, 0).toLocaleString('en-IN')}</td>
                      <td className="py-1.5 text-right tabular-nums">{inviteStats.reduce((sum, row) => sum + (row.link.usedCount || 0), 0).toLocaleString('en-IN')}</td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>
          )}

          {(analytics?.fields || []).length === 0 && (
            <p className="text-sm text-gray-500">
              Tick "Track in analytics dashboard" on form fields (including sub-fields) in the form builder to chart their answers here.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default EventAnalytics;
